import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { parseError } from "../../utils";
import {
  anonUserInterview,
  eligibilityAnalysis,
  eligibilityCountryApprovalRate,
  getAllSession,
  getBusinessChatGptInterviewHistory,
  getBusinessChatGptInterviewVoiceHistory,
  getBusinessClientAllSession,
  getBusinessClientSessionAnalysis,
  getBusinessUserSessionData,
  getSessionAnalysis,
  getUserChatGptInterviewVoiceHistory,
  getUserSessionData,
  saveBusinessInterviewTimeout,
  saveFiBiInterviewRatings,
  saveUserInterviewTimeout,
  uploadFibiDs160,
} from "../../api/ai";
import { aiQueryKeys } from "./types";
import { utilsQueryKeys } from "../utils/types";

export const useEligibilityCountryApprovalRate = () => {
  return useMutation(eligibilityCountryApprovalRate, {
    onError: (error) => parseError(error),
  });
};

export const useEligibilityAnalysis = () => {
  return useMutation(eligibilityAnalysis, {
    onError: (error) => parseError(error),
  });
};

export const useGetBusinessChatGptInterviewVoiceHistory = (
  sessionCount: string,
  businessId: string,
  isEnabled?: boolean
) => {
  return useQuery(
    [
      aiQueryKeys.getBusinessChatGptInterviewVoiceHistory,
      sessionCount,
      businessId,
    ],
    () => getBusinessChatGptInterviewVoiceHistory(sessionCount, businessId),
    {
      enabled: !!sessionCount && isEnabled,
      onError: (error) => parseError(error),
      select: (data) => data.data,
      refetchOnWindowFocus: false,
    }
  );
};

export const useGetUserChatGptInterviewVoiceHistory = (
  sessionCount: string,
  isEnabled?: boolean
) => {
  return useQuery(
    [aiQueryKeys.getUserChatGptInterviewVoiceHistory, sessionCount],
    () => getUserChatGptInterviewVoiceHistory(sessionCount),
    {
      enabled: !!sessionCount && isEnabled,
      onError: (error) => parseError(error),
      select: (data) => data.data,
      refetchOnWindowFocus: false,
    }
  );
};

export const useGetBusinessChatGptInterviewHistory = (
  sessionCount: string,
  businessId: string
) => {
  return useQuery(
    [aiQueryKeys.getBusinessChatGptInterviewHistory, sessionCount, businessId],
    () => getBusinessChatGptInterviewHistory(sessionCount, businessId),
    {
      enabled: !!sessionCount,
      onError: (error) => parseError(error),
      select: (data) => data.data.result,
      refetchOnWindowFocus: false,
    }
  );
};

//
export const useSaveBusinessInterviewTimeout = () => {
  return useMutation(saveBusinessInterviewTimeout, {
    onError: (error) => parseError(error),
  });
};

export const useSaveUserInterviewTimeout = () => {
  return useMutation(saveUserInterviewTimeout, {
    onError: (error) => parseError(error),
  });
};

export const useSaveFiBiInterviewRatings = () => {
  return useMutation(saveFiBiInterviewRatings, {
    onError: (error) => parseError(error),
  });
};

// export const useStreamFiBi = () => {
//   return useMutation({
//     mutationFn: sendPromptToFiBiVoiceText,
//     onError: (error) => parseError(error),
//   });
// };

// export const useStreamFiBiBusiness = () => {
//   return useMutation({
//     mutationFn: sendPromptToFiBiStreamBusiness,
//     onError: (error) => parseError(error),
//   });
// };

export const useUploadFibiDs160 = () => {
  const queryClient = useQueryClient();

  return useMutation(uploadFibiDs160, {
    onError: (error) => parseError(error),
    onSuccess: () =>
      queryClient.invalidateQueries([utilsQueryKeys.getUserData]),
  });
};

export const useGetUserSessionData = (isEnabled?: boolean) => {
  return useQuery([aiQueryKeys.useGetUserSessionData], getUserSessionData, {
    enabled: isEnabled,
    onError: (error) => parseError(error),
    select: (data) => data.data.data,
  });
};

export const useGetBusinessUserSessionData = (
  businessId: string,
  isEnabled?: boolean
) => {
  return useQuery(
    [aiQueryKeys.getBusinessUserSessionData, businessId],
    () => getBusinessUserSessionData(businessId),
    {
      enabled: isEnabled,
      onError: (error) => parseError(error),
      select: (data) => data.data.data,
    }
  );
};

export const useGetSessionAnalysis = (
  sessionCount: string,
  businessId?: string
) => {
  return useQuery(
    [aiQueryKeys.getSessionAnalysis, sessionCount, businessId],
    () => getSessionAnalysis(sessionCount, businessId),
    {
      onError: (error) => parseError(error),
      select: (data) => data.data.data,
    }
  );
};

export const useGetBusinessClientSessionAnalysis = (
  sessionCount: string,
  buid: string,
  isEnabled?: boolean
) => {
  return useQuery(
    [aiQueryKeys.getBusinessClientSessionAnalysis, sessionCount, buid],
    () => getBusinessClientSessionAnalysis(sessionCount, buid),
    {
      enabled: !!buid && isEnabled,
      onError: (error) => parseError(error),
      select: (data) => data.data.data,
    }
  );
};

export const useGetAllSession = () => {
  return useQuery([aiQueryKeys.getAllSession], () => getAllSession(), {
    onError: (error) => parseError(error),
    select: (data) => data.data.data,
  });
};

export const useGetBusinessClientAllSession = (buid: string) => {
  return useQuery(
    [aiQueryKeys.getBusinessClientAllSession, buid],
    () => getBusinessClientAllSession(buid),
    {
      enabled: !!buid,
      onError: (error) => parseError(error),
      select: (data) => data.data.data,
    }
  );
};

export const useAnonUserInterview = () => {
  return useMutation(anonUserInterview, {
    onError: (error) => parseError(error),
  });
};
